import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Dialog } from '@mui/material';
import { selectModal, closeModal } from '../../store/slices/uiSlice';

// Import modal components
import EmployeeFormModal from '../modals/EmployeeFormModal';
import DepartmentFormModal from '../modals/DepartmentFormModal';
import TimeOffFormModal from '../modals/TimeOffFormModal';
import ConfirmationModal from '../modals/ConfirmationModal';
import EmailPreviewModal from '../modals/EmailPreviewModal';

// Modal type mapping
const MODAL_COMPONENTS: Record<string, React.ComponentType<any>> = {
  'employee-form': EmployeeFormModal,
  'department-form': DepartmentFormModal,
  'time-off-form': TimeOffFormModal,
  'confirmation': ConfirmationModal,
  'email-preview': EmailPreviewModal,
};

/**
 * Modals that save from inside and report it through `onPendingChange`, so
 * Escape and the backdrop can't dismiss them while a request is in flight.
 */
const PENDING_AWARE_MODALS = new Set(['confirmation', 'time-off-form']);

const ModalManager: React.FC = () => {
  const { isOpen, type, data } = useSelector(selectModal);
  const dispatch = useDispatch();
  const [modalPending, setModalPending] = useState(false);
  const pendingRef = useRef(false);

  // A request in flight belongs to the modal that started it; any open, close
  // or switch of modal starts clean.
  useEffect(() => {
    pendingRef.current = false;
    setModalPending(false);
  }, [isOpen, type]);

  const handlePendingChange = (pending: boolean) => {
    pendingRef.current = pending;
    setModalPending(pending);
  };

  const handleClose = () => {
    dispatch(closeModal());
  };

  if (!isOpen || !type) {
    return null;
  }

  // Get the appropriate modal component
  const ModalComponent = MODAL_COMPONENTS[type];

  // If the modal type doesn't exist, return null
  if (!ModalComponent) {
    console.error(`Modal type "${type}" not found`);
    return null;
  }

  return (
    <Dialog
      open={isOpen}
      onClose={() => { if (!pendingRef.current) handleClose(); }}
      disableEscapeKeyDown={modalPending}
      maxWidth={type === 'confirmation' ? 'xs' : 'md'}
      fullWidth
      aria-labelledby={type === 'confirmation' ? 'confirmation-title' : undefined}
      aria-describedby={type === 'confirmation' ? 'confirmation-message' : undefined}
    >
      <ModalComponent onClose={handleClose} {...data} {...(PENDING_AWARE_MODALS.has(type) ? { onPendingChange: handlePendingChange } : {})} />
    </Dialog>
  );
};

export default ModalManager;
